import mqtt from 'mqtt';
import { useStore } from '../store';

class MQTTClient {
  private client: mqtt.MqttClient | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private locationData: { [key: string]: any } = {};
  private serverUrl: string;
  private connectionAttempts = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;

  constructor() {
    const host = import.meta.env.VITE_MQTT_HOST;
    const port = import.meta.env.VITE_MQTT_PORT;
    const path = import.meta.env.VITE_MQTT_PATH;

    if (!host || !port || !path) {
      console.error('MQTT configuration missing');
      return;
    }

    this.serverUrl = `wss://${host}:${port}${path}`;
  }

  connect() {
    if (this.isConnecting || this.client?.connected) {
      console.log('MQTT: Already connected or connecting');
      return;
    }

    this.isConnecting = true;
    console.log('MQTT: Attempting to connect to', this.serverUrl);

    try {
      this.client = mqtt.connect(this.serverUrl, {
        clientId: `marathon-tracker-${Math.random().toString(16).substr(2, 8)}`,
        clean: true,
        reconnectPeriod: 5000,
        connectTimeout: 30000,
        keepalive: 60,
        resubscribe: true,
      });

      this.client.on('connect', () => {
        console.log('MQTT: Connected successfully');
        this.isConnecting = false;
        this.connectionAttempts = 0;
        this.subscribeToTopics();
      });

      this.client.on('reconnect', () => {
        console.log('MQTT: Attempting to reconnect...');
      });

      this.client.on('message', (topic: string, message: Buffer) => {
        console.log('MQTT: Received message on topic:', topic);
        this.handleMessage(topic, message);
      });

      this.client.on('error', (error) => {
        console.error('MQTT error:', error);
        this.handleDisconnect();
      });

      this.client.on('close', () => {
        console.log('MQTT: Connection closed');
        this.handleDisconnect();
      });

      this.client.on('offline', () => {
        console.log('MQTT: Client went offline');
        this.handleDisconnect();
      });

    } catch (error) {
      console.error('MQTT: Error connecting:', error);
      this.handleDisconnect();
    }
  }

  private handleDisconnect() {
    this.isConnecting = false;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.connectionAttempts++;
    console.log(`MQTT: Reconnection attempt ${this.connectionAttempts} of ${this.MAX_RECONNECT_ATTEMPTS}`);

    if (this.connectionAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      const delay = Math.min(1000 * Math.pow(2, this.connectionAttempts), 30000);
      console.log(`MQTT: Will attempt to reconnect in ${delay}ms`);
      
      this.reconnectTimer = setTimeout(() => {
        console.log('MQTT: Attempting to reconnect...');
        this.connect();
      }, delay);
    } else {
      console.error('MQTT: Max reconnection attempts reached');
      this.reset();
    }
  }

  private reset() {
    console.log('MQTT: Resetting client state');
    if (this.client) {
      this.client.end(true);
      this.client = null;
    }
    this.isConnecting = false;
    this.connectionAttempts = 0;
    this.locationData = {};
  }

  private async subscribeToTopics() {
    if (!this.client?.connected) {
      console.error('MQTT: Cannot subscribe - client not connected');
      return;
    }

    const users = useStore.getState().users;
    console.log('MQTT: Subscribing for users:', users);

    for (const user of users) {
      if (user.topic) {
        console.log('MQTT: Subscribing to topic:', user.topic);
        const success = await this.subscribe(user.topic);
        if (success) {
          console.log('MQTT: Successfully subscribed to:', user.topic);
        } else {
          console.error('MQTT: Failed to subscribe to:', user.topic);
        }
      }
    }
  }

  private subscribe(topic: string) {
    if (!this.client?.connected) {
      console.error('MQTT: Cannot subscribe - client not connected');
      return false;
    }

    return new Promise<boolean>((resolve) => {
      this.client!.subscribe(topic, { qos: 1 }, (error) => {
        if (error) {
          console.error('MQTT: Error subscribing to topic:', topic, error);
          resolve(false);
        } else {
          console.log('MQTT: Successfully subscribed to topic:', topic);
          resolve(true);
        }
      });
    });
  }

  private handleMessage(topic: string, message: Buffer) {
    try {
      const data = JSON.parse(message.toString());
      const users = useStore.getState().users;
      const user = users.find(u => u.topic === topic);
      
      if (!user) {
        console.log('MQTT: No matching user found for topic:', topic);
        return;
      }

      if (!data.lat || !data.lon) {
        console.log('MQTT: Missing lat/lon in message for topic:', topic);
        return;
      }

      const position: [number, number] = [data.lon, data.lat];
      const speed = typeof data.vel === 'number' ? data.vel : 0;
      const course = typeof data.cog === 'number' ? data.cog : 0;
      
      useStore.getState().updateUserPosition(user.id, position, speed, course);

      const extendedData = {
        ...data,
        _type: "location",
        _id: Math.random().toString(16).substring(2, 10),
        acc: data.acc || 0,
        alt: data.alt || 0,
        batt: data.batt || 100,
        bs: data.bs || 1,
        cog: course,
        conn: data.conn || "w",
        created_at: Math.floor(Date.now() / 1000),
        tid: data.tid || "ar",
        tst: Math.floor(Date.now() / 1000),
        vac: data.vac || 1,
        vel: speed,
        speed: speed,
        name: user.name,
        zenku: user.lastRouteStatus?.onRoute ? 
          (user.lastRouteStatus.name ? parseInt(user.lastRouteStatus.name.replace(/\D/g, '')) || 0 : 0) : 
          "---",
        kukyori: user.lastRouteStatus?.onRoute ? 
          user.lastRouteStatus.distanceToGoal.toFixed(3) : 
          "---",
        oddc: user.lastRouteStatus?.onRoute ? 
          user.lastRouteStatus.distanceFromStart.toFixed(3) : 
          "0.000",
        latx: data.lat,
        lony: data.lon,
        area: "---",
        elevation: data.alt || 0,
        kmh: speed
      };

      const userId = `user-${user.name.replace(/[^0-9]/g, '')}`;
      this.locationData[userId] = extendedData;

      if (this.client?.connected) {
        const publishTopic = `${topic}/extended`;
        this.client.publish(publishTopic, JSON.stringify(extendedData), { qos: 1 });
      }
    } catch (error) {
      console.error('MQTT: Error processing message:', error);
    }
  }

  getLocationData(userId: string) {
    return this.locationData[userId];
  }

  getAllLocationData() {
    return this.locationData;
  }

  disconnect() {
    console.log('MQTT: Disconnecting client');
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.client) {
      this.client.end(true);
      this.client = null;
    }
    this.isConnecting = false;
    this.connectionAttempts = 0;
    this.locationData = {};
  }

  isConnected() {
    return this.client?.connected || false;
  }

  reconnect() {
    this.disconnect();
    this.connect();
  }
}

export const mqttClient = new MQTTClient();